/**
 * `/settings` — the hub (C11, FR-A8 §1; T-1.17).
 *
 * A grid of cards, grouped, each with an icon, a title, what the page is for,
 * and **one line of what is set now**. The setup alert stays above the grid.
 * Every card opens its own page; nothing is edited here.
 *
 * One read, the service's summary (one line per card). A card whose line
 * could not be read shows its description and no line — never a stale or
 * invented one. The card itself is `components/HubCard`, shared with the
 * Website hub since Wave 14.
 *
 * **Wave 14** (C13, ADR-0032): *Client sign-in* and *Domain* moved to *Your
 * website*; one row at the foot links there, naming what moved.
 */

import { Alert, Badge, EmptyState, PageHeader } from '@fiveox/ui'
import { Globe } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'

import { HubCard, HubGroup, HubRow } from '../../../components/HubCard'
import { SETTINGS, useEvaluation } from '../../../lib/access'
import { type SettingsSummary, firm } from '../../../lib/api/firm'
import { MOVED_TO_WEBSITE, legacyTarget, visibleCards } from './cards'

export function SettingsHub() {
  const { can, evaluation } = useEvaluation()
  const location = useLocation()
  const navigate = useNavigate()
  const [summary, setSummary] = useState<SettingsSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  // An old `/settings#branches` lands on its page (T-1.20).
  useEffect(() => {
    const target = legacyTarget(location.hash)
    if (target) navigate(target, { replace: true })
  }, [location.hash, navigate])

  useEffect(() => {
    let cancelled = false
    firm
      .settingsSummary()
      .then((body) => !cancelled && setSummary(body.data))
      .catch(() => !cancelled && setFailed(true))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [])

  if (evaluation && !can(SETTINGS, 'can_view')) {
    return (
      <EmptyState
        title="Settings aren’t open to you"
        description="Ask a partner or your practice manager — they can change the firm’s settings."
      />
    )
  }

  const groups = visibleCards({ summary })

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8">
      <PageHeader
        title="Settings"
        description="Your firm, and how ProDesk behaves for it. Each card opens its own page."
        meta={
          !can(SETTINGS, 'can_update') && evaluation ? (
            <Badge tone="neutral" size="sm">
              View only
            </Badge>
          ) : undefined
        }
      />

      {summary && !summary.is_onboarded && (
        // The second way back into setup; the Overview's own checklist is the
        // first. Kept because a partner who lands here to change the GSTIN is
        // exactly the person who abandoned it.
        <Alert tone="info">
          Your practice setup isn’t finished.{' '}
          <Link to="/onboarding" className="font-medium underline">
            Continue where you left off
          </Link>
          .
        </Alert>
      )}

      {failed && (
        <Alert tone="warning">
          We couldn’t read what’s set just now. Every page still opens — the lines under each card
          will be back when the read is.
        </Alert>
      )}

      {groups.map(({ group, cards }) => (
        <HubGroup
          key={group.key}
          columns={cards.length % 3 === 0 || cards.length === 1 ? 3 : 2}
          id={`settings-${group.key}`}
          title={group.title}
          description={group.description}
        >
          {cards.map((card) => (
            <HubCard
              key={card.key}
              cardKey={card.key}
              href={card.href}
              icon={card.icon}
              title={card.title}
              description={card.description}
              loading={loading}
              line={card.summary({ summary })}
            />
          ))}
        </HubGroup>
      ))}

      {/* Wave 14 (C13, ADR-0032 §1–§2): what faces a visitor is edited under
          Your website. One row, naming what moved, so a firm that learnt
          them here finds them. */}
      <section aria-labelledby="settings-website" className="space-y-3">
        <h2
          id="settings-website"
          className="text-muted-foreground text-xs font-semibold tracking-wider uppercase"
        >
          Your website
        </h2>
        <HubRow
          rowKey="website"
          href="/site"
          icon={Globe}
          title="Your website"
          description={`${MOVED_TO_WEBSITE.join(', ')} are edited here now, beside your template and pages.`}
        />
      </section>
    </div>
  )
}
